import { CboRecordDto, ResourceDto } from '@act/shared/data-transfer-objects';
import type { ActionFormResourceDetails } from 'react-components/dialog-forms/components/useNeedActionFormtypes';
import type { CboResourceFormValues } from './cboResourceForm.schema';
import type { ResourceCardProps } from '@act/storybook';
import type {
  NeedsActionDto,
  NeedsResourceDetailsDto,
} from '@act/shared/data-transfer-objects';
import {
  getResourceCardIconForNeed,
  type ResourceCardNeedIconOptions,
} from './resourceCardIconForNeed.util';

/**
 * Maps partial CBO / Places values into need-action resource detail fields.
 *
 * @param partial - Autofill values from Places or CBO cache.
 * @returns Partial resource referral row.
 */
export function mapPlacePartialToResourceDetails(
  partial: Partial<CboResourceFormValues>
): Partial<ActionFormResourceDetails> {
  const addressLine = partial.address?.trim();
  return {
    resourceName: partial.name ?? '',
    contactName: partial.contactName ?? '',
    phoneNumber: partial.phoneNumber ?? '',
    email: partial.email ?? '',
    url: partial.websiteUrl ?? '',
    streetAddress: partial.streetAddress || addressLine || '',
    city: partial.city ?? '',
    state: partial.state ?? '',
    zipcode: partial.zipcode ?? '',
    googlePlaceId: partial.googlePlaceId ?? null,
    formattedAddress: partial.formattedAddress || addressLine || '',
    addressDetails: partial.addressDetails ?? '',
    placeTypes: partial.placeTypes ?? [],
    categoryLabel: partial.categoryLabel ?? '',
    isCustomResource: partial.isCustomResource ?? false,
    cboRecordId: partial.cboRecordId ?? undefined,
  };
}

/**
 * Maps a persisted CBO record into need-action resource detail fields.
 *
 * @param record - Server CBO DTO.
 * @returns Partial resource referral row.
 */
export function mapCboRecordToResourceDetails(
  record: CboRecordDto
): Partial<ActionFormResourceDetails> {
  return mapPlacePartialToResourceDetails({
    cboRecordId: record.id,
    name: record.name,
    contactName: record.contactName ?? '',
    phoneNumber: record.phoneNumber ?? '',
    email: record.email ?? '',
    websiteUrl: record.websiteUrl ?? '',
    streetAddress: record.streetAddress ?? '',
    city: record.city ?? '',
    state: record.state ?? '',
    zipcode: record.zipcode ?? '',
    googlePlaceId: record.googlePlaceId,
    formattedAddress: record.formattedAddress ?? '',
    categoryLabel: record.categoryLabel ?? '',
    placeTypes: record.placeTypes ?? [],
    addressDetails: record.addressDetails ?? '',
  });
}

type ResourceDetailsLike = Pick<
  ActionFormResourceDetails,
  | 'resourceName'
  | 'categoryLabel'
  | 'contactName'
  | 'phoneNumber'
  | 'url'
  | 'streetAddress'
  | 'addressDetails'
  | 'city'
  | 'state'
  | 'zipcode'
  | 'formattedAddress'
>;

/**
 * Builds a display address from legacy `resource_details` rows or newer CBO-enriched rows.
 *
 * @param details - Resource row or history DTO.
 * @returns Combined address line, or undefined when empty.
 */
function buildResourceDisplayAddress(
  details: ResourceDetailsLike | NeedsResourceDetailsDto
): string | undefined {
  if ('formattedAddress' in details && details.formattedAddress?.trim()) {
    return details.formattedAddress.trim();
  }

  const addressDetails =
    'addressDetails' in details ? details.addressDetails : undefined;
  const streetLine = [details.streetAddress, addressDetails]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');

  const localityLine = [
    'city' in details ? details.city : '',
    details.state,
    details.zipcode,
  ]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(', ');

  const combined = [streetLine, localityLine].filter(Boolean).join(', ');
  return combined || undefined;
}

function buildLegacyResourceDisplayAddress(
  address: ResourceDto['address']
): string | undefined {
  if (!address) {
    return undefined;
  }

  const streetLine = [address.line1, address.line2]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');

  const localityLine = [address.city, address.state, address.zip]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(', ');

  const combined = [streetLine, localityLine].filter(Boolean).join(', ');
  return combined || undefined;
}

/**
 * Collects unique resource referrals from an action and its subsequent actions.
 *
 * @param action - Need action (may include nested subsequent actions).
 * @returns De-duplicated referrals; legacy rows without `id` use name + createdAt.
 */
export function getUniqueResourceReferralsFromAction(
  action: NeedsActionDto
): NeedsResourceDetailsDto[] {
  const allResourceReferrals = [
    ...(action.resourceReferrals ?? []),
    ...(action.subsequentActions ?? []).flatMap(
      subsequent => subsequent.resourceReferrals ?? []
    ),
  ];

  const uniqueByKey = new Map<string | number, NeedsResourceDetailsDto>();

  for (const referral of allResourceReferrals) {
    const key =
      referral.id ??
      `legacy-${referral.resourceName}-${referral.createdAt ?? ''}`;
    if (!uniqueByKey.has(key)) {
      uniqueByKey.set(key, referral);
    }
  }

  return [...uniqueByKey.values()];
}

/**
 * Maps a persisted need resource referral into the action form row shape.
 *
 * @param resource - Legacy or current API resource referral.
 * @returns Form row used by need-action resource fields.
 */
export function mapLegacyResourceReferralToFormRow(
  resource: NeedsResourceDetailsDto
): ActionFormResourceDetails {
  return {
    id: resource.id,
    resourceName: resource.resourceName,
    contactName: resource.contactName || '',
    phoneNumber: resource.phoneNumber || '',
    email: resource.email || '',
    url: resource.url || '',
    streetAddress: resource.streetAddress || '',
    addressDetails: resource.addressDetails || '',
    city: resource.city || '',
    state: resource.state || '',
    zipcode: resource.zipcode || '',
    formattedAddress: buildResourceDisplayAddress(resource) ?? '',
    shouldDelete: false,
  };
}

/**
 * Builds {@link ResourceCard} props from a resource referral or history DTO.
 *
 * @param details - Resource row or history DTO.
 * @param needIconOptions - Optional need issue context for the decorative icon.
 * @returns Props for {@link ResourceCard}.
 */
export function mapResourceDetailsToResourceCardProps(
  details: ResourceDetailsLike | NeedsResourceDetailsDto,
  needIconOptions?: ResourceCardNeedIconOptions
): Pick<
  ResourceCardProps,
  | 'name'
  | 'category'
  | 'contactName'
  | 'phone'
  | 'website'
  | 'address'
  | 'iconName'
> {
  const category =
    'categoryLabel' in details && details.categoryLabel
      ? details.categoryLabel
      : undefined;

  return {
    name: details.resourceName,
    category,
    contactName: details.contactName?.trim() || undefined,
    phone: details.phoneNumber?.trim() || undefined,
    website: details.url?.trim() || undefined,
    address: buildResourceDisplayAddress(details),
    iconName: getResourceCardIconForNeed(needIconOptions),
  };
}

/**
 * Builds {@link ResourceCard} props from the shared CBO resource form values.
 *
 * @param values - Current CBO resource form values.
 * @param iconName - Optional resource card icon override.
 * @returns Props for {@link ResourceCard}.
 */
export function mapCboFormValuesToResourceCardProps(
  values: CboResourceFormValues,
  iconName?: ResourceCardProps['iconName']
): Pick<
  ResourceCardProps,
  | 'name'
  | 'category'
  | 'contactName'
  | 'phone'
  | 'website'
  | 'address'
  | 'iconName'
> {
  const address =
    values.formattedAddress?.trim() ||
    buildResourceDisplayAddress({
      resourceName: values.name,
      categoryLabel: values.categoryLabel ?? '',
      contactName: values.contactName ?? '',
      phoneNumber: values.phoneNumber ?? '',
      url: values.websiteUrl ?? '',
      streetAddress: values.streetAddress || values.address || '',
      addressDetails: values.addressDetails ?? '',
      city: values.city ?? '',
      state: values.state ?? '',
      zipcode: values.zipcode ?? '',
      formattedAddress: values.formattedAddress ?? '',
    });

  return {
    name: values.name,
    category: values.categoryLabel?.trim() || undefined,
    contactName: values.contactName?.trim() || undefined,
    phone: values.phoneNumber?.trim() || undefined,
    website: values.websiteUrl?.trim() || undefined,
    address,
    iconName,
  };
}

/**
 * Builds {@link ResourceCard} props from the legacy resource interaction DTO.
 *
 * @param resource - Resource interaction row from legacy call/visit history.
 * @param needIconOptions - Optional need issue context for the decorative icon.
 * @returns Props for {@link ResourceCard}.
 */
export function mapLegacyResourceToResourceCardProps(
  resource: ResourceDto,
  needIconOptions?: ResourceCardNeedIconOptions
): Pick<ResourceCardProps, 'name' | 'phone' | 'address' | 'iconName'> {
  return {
    name: resource.title,
    phone: resource.phoneNumber?.trim() || undefined,
    address: buildLegacyResourceDisplayAddress(resource.address),
    iconName: getResourceCardIconForNeed(needIconOptions),
  };
}

/**
 * Builds {@link ResourceCard} props from a persisted CBO record.
 *
 * @param record - Canonical CBO record linked to a call or referral.
 * @param needIconOptions - Optional need issue context for the decorative icon.
 * @returns Props for {@link ResourceCard}.
 */
export function mapCboRecordToResourceCardProps(
  record: CboRecordDto,
  needIconOptions?: ResourceCardNeedIconOptions
): Pick<
  ResourceCardProps,
  | 'name'
  | 'category'
  | 'contactName'
  | 'phone'
  | 'website'
  | 'address'
  | 'iconName'
> {
  return {
    name: record.name,
    category: record.categoryLabel ?? undefined,
    contactName: record.contactName ?? undefined,
    phone: record.phoneNumber ?? undefined,
    website: record.websiteUrl ?? undefined,
    address:
      buildResourceDisplayAddress({
        resourceName: record.name,
        categoryLabel: record.categoryLabel ?? '',
        contactName: record.contactName ?? '',
        phoneNumber: record.phoneNumber ?? '',
        url: record.websiteUrl ?? '',
        streetAddress: record.streetAddress ?? '',
        addressDetails: record.addressDetails ?? '',
        city: record.city ?? '',
        state: record.state ?? '',
        zipcode: record.zipcode ?? '',
        formattedAddress: record.formattedAddress ?? '',
      }) ?? undefined,
    iconName: getResourceCardIconForNeed(needIconOptions),
  };
}
